import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import {
  useTheme,
  MenuItem,
  Select,
  FormControl,
  useMediaQuery,
  Box,
  Typography,
  Divider,
  Stack,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CustomModal from '../../CustomModal';
import Iconify from '../../Iconify';
import CustomDrawer from '../../CustomDrawer';
import TransactionDetails from './TransactionDetails';
import { fDateTimeSQL } from '../../../utils/formatTime';
import useLocales from '../../../hooks/useLocales';
import { conObjForm } from '../../../utils/connection-assistant';

const TransactionHistoryDialog = ({ handleClose, handleOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  // 1. STANDARDIZED OPTIONS
  const TIME_OPTIONS = ['Today', 'Yesterday', 'Last 3 Days', 'Last 7 Days', 'Last 15 Days', 'Last 30 Days'];

  const timeTranslations = {
    'Last 3 Days': 'last_three_days',
    'Last 7 Days': 'last_seven_days',
    'Last 30 Days': 'last_30_days',
    'Last 15 Days': 'last_15_days',
  };
  const getStatusColor = (status) => {
    if (status === 'Approve' || status === 'Completed') return '#16A34A';
    if (status === 'Reject' || status === 'Timeout' || status === 'Canceled') return '#DC2626';
    if (status === 'Pending') return '#F59E0B';
    return '#6B7280';
  };

  const selectStyle = {
    backgroundColor: '#F3F4F6',
    borderRadius: '12px',
    fontSize: 14,
    fontWeight: 500,
    height: 40,
    '& fieldset': { border: 'none' },
  };

  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('LIST');
  const [selectedTx, setSelectedTx] = useState(null);
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [timeFilter, setTimeFilter] = useState('Last 3 Days');
  const [drawerConfig, setDrawerConfig] = useState({ open: false, type: '' });
  const [rawTransactions, setRawTransactions] = useState([]);

  const fetchTransactionHistory = useCallback(async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');

      const now = new Date();
      const start = new Date();
      start.setDate(now.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      now.setHours(23, 59, 59, 999);

      const formatDate = (date) => {
        const pad = (num) => String(num).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
          date.getMinutes()
        )}:${pad(date.getSeconds())}`;
      };

      const payload = {
        'view_report[start_date]': formatDate(start),
        'view_report[end_date]': formatDate(now),
      };

      const { data: response } = await conObjForm.post('v1/report', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.success) {
        setRawTransactions(response.data || []);
      }
    } catch (error) {
      enqueueSnackbar('Error fetching transaction history', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (handleOpen) fetchTransactionHistory();
  }, [handleOpen, fetchTransactionHistory]);

  // Remove the outside scroll of the transaction history in mobile view
  useEffect(() => {
    if (handleOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [handleOpen]);

  const filteredTransactions = useMemo(() => {
    let data = [...rawTransactions];

    if (typeFilter !== 'ALL') {
      data = data.filter((item) => item.type === typeFilter);
    }
    const now = new Date();
    const filterStart = new Date();
    filterStart.setHours(0, 0, 0, 0);

    if (timeFilter === 'Today') {
      // filterStart is already today 00:00
    } else if (timeFilter === 'Yesterday') {
      filterStart.setDate(now.getDate() - 1);
      const yesterdayEnd = new Date();
      yesterdayEnd.setDate(now.getDate() - 1);
      yesterdayEnd.setHours(23, 59, 59, 999);

      return data.filter((item) => {
        const itemDate = new Date(item.created_at.replace(' ', 'T'));
        return itemDate >= filterStart && itemDate <= yesterdayEnd;
      });
    } else if (timeFilter === 'Last 3 Days') {
      filterStart.setDate(now.getDate() - 3);
    } else if (timeFilter === 'Last 7 Days') {
      filterStart.setDate(now.getDate() - 7);
    } else if (timeFilter === 'Last 15 Days') {
      filterStart.setDate(now.getDate() - 15);
    } else {
      filterStart.setDate(now.getDate() - 30);
    }

    return data.filter((item) => new Date(item.created_at.replace(' ', 'T')) >= filterStart);
  }, [rawTransactions, typeFilter, timeFilter]);

  const handleBack = () => {
    if (view === 'DETAILS') setView('LIST');
    else if (isMobile) handleClose();
  };

  const MainContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: isMobile ? '100vh' : 600, bgcolor: '#fff', pb: 4 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {(isMobile || view === 'DETAILS') && (
          <IconButton onClick={handleBack} sx={{ position: 'absolute', left: 8 }}>
            <Iconify icon="material-symbols:arrow-back-ios-new-rounded" />
          </IconButton>
        )}
        <Typography variant="h6" fontWeight={700}>
          {view === 'LIST' ? translate('transaction_history') : translate('details')}
        </Typography>
        {view === 'LIST' && !isMobile && (
          // <IconButton onClick={fetchTransactionHistory} disabled={loading} sx={{ position: 'absolute', right: 8 }}>
          //   <Iconify icon="solar:refresh-linear" className={loading ? 'animate-spin' : ''} width={20} />
          // </IconButton>
          <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8 }}>
            {/* Using the standard close/X icon */}
            <Iconify icon="material-symbols:close-rounded" width={24} />
          </IconButton>
        )}
      </Box>

      {view === 'LIST' ? (
        <>
          <Stack direction="row" spacing={1.5} sx={{ px: 2, mb: 2 }}>
            {isMobile ? (
              <>
                <Box
                  onClick={() => setDrawerConfig({ open: true, type: 'TYPE' })}
                  sx={{
                    flex: 1,
                    p: 1.2,
                    bgcolor: '#F3F4F6',
                    borderRadius: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Typography fontSize={14} fontWeight={500}>
                    {translate(typeFilter === 'ALL' ? 'all_type' : typeFilter)}
                  </Typography>
                  <Iconify icon="eva:chevron-down-fill" width={18} />
                </Box>
                <Box
                  onClick={() => setDrawerConfig({ open: true, type: 'TIME' })}
                  sx={{
                    flex: 1,
                    p: 1.2,
                    bgcolor: '#F3F4F6',
                    borderRadius: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Typography fontSize={14} fontWeight={500}>
                    {translate(timeTranslations[timeFilter] || timeFilter)}
                  </Typography>
                  <Iconify icon="eva:chevron-down-fill" width={18} />
                </Box>
              </>
            ) : (
              <>
                <FormControl fullWidth>
                  <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={selectStyle}>
                    <MenuItem value="ALL">{translate('all_type')}</MenuItem>
                    <MenuItem value="Deposit">{translate('deposit')}</MenuItem>
                    <MenuItem value="Withdraw">{translate('withdraw')}</MenuItem>
                    <MenuItem value="Transfer">{translate('transfer')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)} sx={selectStyle}>
                    {TIME_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {translate(timeTranslations[opt] || opt)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>

          <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2 }}>
            {loading && (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
                <CircularProgress size={30} />
              </Stack>
            )}
            {!loading && filteredTransactions.length === 0 && (
              <Typography textAlign="center" color="text.secondary" sx={{ py: 5 }}>
                {translate('no_record_found')}
              </Typography>
            )}
            {!loading &&
              filteredTransactions.length > 0 &&
              filteredTransactions.map((item, index) => (
                <React.Fragment key={item.transaction_id}>
                  <Box
                    onClick={() => {
                      setSelectedTx(item);
                      setView('DETAILS');
                    }}
                    sx={{ display: 'flex', justifyContent: 'space-between', py: 2, cursor: 'pointer' }}
                  >
                    <Box>
                      <Typography fontSize={16}>{translate(item.type)}</Typography>
                      <Typography fontSize={12} color="#B2B7C7">
                        {fDateTimeSQL(item.created_at)}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography fontSize={16} fontWeight={700}>
                        {item.amount}
                      </Typography>
                      <Typography fontSize={12} color={getStatusColor(item.status)}>
                        {translate(item.status)}
                      </Typography>
                    </Box>
                  </Box>
                  {index !== filteredTransactions.length - 1 && <Divider />}
                </React.Fragment>
              ))}
          </Box>
        </>
      ) : (
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          <TransactionDetails embedded data={selectedTx} />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: theme.zIndex.modal, bgcolor: '#fff' }}>{MainContent}</Box>
      ) : (
        <CustomModal open={handleOpen} onClose={handleClose} maxWidth="xs" hasClose={false}>
          {MainContent}
        </CustomModal>
      )}

      <CustomDrawer
        open={drawerConfig.open}
        onClose={() => setDrawerConfig({ ...drawerConfig, open: false })}
        title={drawerConfig.type === 'TYPE' ? `${translate('type')}` : `${translate('time')}`}
        sx={{ zIndex: theme.zIndex.modal + 1100 }}
      >
        <Box sx={{ p: 2, pb: 6, height: '480px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            {drawerConfig.type === 'TYPE'
              ? ['All Type', 'Deposit', 'Withdraw', 'Transfer'].map((option) => (
                  <Box
                    key={option}
                    onClick={() => {
                      setTypeFilter(option === 'All Type' ? 'ALL' : option);
                      setDrawerConfig({ ...drawerConfig, open: false });
                    }}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      fontSize: 14,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      bgcolor:
                        typeFilter === option || (option === 'All Type' && typeFilter === 'ALL')
                          ? '#353845'
                          : '#F3F4F6',
                      color:
                        typeFilter === option || (option === 'All Type' && typeFilter === 'ALL') ? '#fff' : '#212B36',
                      fontWeight: 600,
                    }}
                  >
                    {translate(option)}
                  </Box>
                ))
              : TIME_OPTIONS.map((option) => (
                  <Box
                    key={option}
                    onClick={() => {
                      setTimeFilter(option);
                      setDrawerConfig({ ...drawerConfig, open: false });
                    }}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      fontSize: 14,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      bgcolor: timeFilter === option ? '#353845' : '#F3F4F6',
                      color: timeFilter === option ? '#fff' : '#212B36',
                      fontWeight: 600,
                    }}
                  >
                    {translate(timeTranslations[option] || option)}
                  </Box>
                ))}
          </Box>
        </Box>
      </CustomDrawer>
    </>
  );
};

export default TransactionHistoryDialog;
